import { z } from "zod";
import { themeStylesSchema } from "./theme-style.js";
import { themeColorPaletteNameSchema } from "./color-palette.js";
import { sceneConfigSchema } from "./scene.js";
import { backgroundSchema } from "./background.js";
export const compilationThemeIds = [
    "birthday-balloons",
    "wedding-guest-book",
    "inky-guest-book",
    "birthday-cake",
    "wedding-cream-and-gold",
    "wedding-garden-script",
    "wedding-spring-greenery",
    "wedding-natural",
    "wedding-garden-rose",
    "general-solid",
    "diamond-congrats",
    "vintage-symmetry",
    "pop-hearts",
    "paint-daubs",
    "birthday-candles",
    "doodle-coffee",
    "star-pop",
    "meadow-flowers",
    "mountain-view",
    "big-congrats",
    "windflower",
    "school-spirit",
    "christmas-presents",
    "holiday-snow",
];
// If we ever store the themes in a database, we won't be able to strongly type
// the IDs. But for now, we can.
export const compilationThemeIdSchema = z.enum(compilationThemeIds);
export const compilationThemeVersionSchema = z.number().optional();
const userInputFieldsCommonSchema = z.object({
    id: z.string(),
    label: z.string(),
    placeholder: z.string().optional(),
    helpText: z.string().optional(),
    required: z.boolean().optional(),
    defaultValue: z.string().optional(),
    // If the default value of this field depends on another, indicate it here
    parentId: z.string().optional(),
    // Where to seek to in the preview when editing this user input field
    preview: z
        .object({
        sceneIndex: z.number(),
        frameOffset: z.number().optional(),
    })
        .optional(),
});
const userInputFieldSchema = z.union([
    userInputFieldsCommonSchema.extend({
        type: z.enum(["text", "textarea"]),
        min: z.number(),
        max: z.number(),
    }),
    userInputFieldsCommonSchema.extend({
        type: z.literal("number"),
        min: z.number().optional(),
        max: z.number().optional(),
    }),
    userInputFieldsCommonSchema.extend({
        type: z.literal("date"),
        /** A format accepted by date-fns formatDate function **/
        dateFormat: z.string().optional(),
    }),
    userInputFieldsCommonSchema.extend({
        type: z.literal("colorPicker"),
    }),
]);
/**
 *  In the event that there are breaking changes in the theme, there are two
 *  possible solutions:
 *  1. Duplicate the schema, increment the version, and handle both the old and new
 *     versions until we're certain the old version is no longer in use.
 *  2. Add `.transform` to compilationThemeSchema that validates data from the old version
 *     and transforms it into the new version.
 *
 * Example:
 *   compilationThemeSchema = compilationThemeSchema.transform((val, ctx) => {
 *     val.scenes.forEach((scene, i) => {
 *       if (scene.type === "introGreeting") {
 *         scene.type = "greeting";
 *         scene.variant = "intro";
 *       } else if (scene.type === "outroGreeting") {
 *         scene.type = "greeting";
 *         scene.variant = "outro";
 *       } else if (scene.type !== "greeting") {
 *         ctx.addIssue({
 *           code: z.ZodIssueCode.custom,
 *           message: "Scene type is invalid",
 *           path: ["scenes", i, "type"],
 *         });
 *       }
 *     })
 *   })
 **/
export const compilationThemeSchema = z.object({
    version: compilationThemeVersionSchema,
    themeId: compilationThemeIdSchema,
    name: z.string(),
    thumbnailUrl: z.string(),
    endFadeDuration: z.number().optional().default(0.5),
    thumbnailFrame: z.number(),
    background: backgroundSchema.array(),
    // This is to avoid error TS7056: The inferred type of this node exceeds the
    // maximum length the compiler will serialize. This keeps the reference to
    // scenes on the theme, but doesn't parse them by default. Use
    // `checkThemeUnknowns` below if you need to access scenes.
    scenes: z.array(z.unknown()),
    // This is to avoid error TS7056: The inferred type of this node exceeds the
    // maximum length the compiler will serialize. This keeps the reference to
    // scenes on the theme, but doesn't parse them by default. Use
    // `checkThemeUnknowns` below if you need to access styles.
    styles: z.unknown(),
    userInputFields: userInputFieldSchema.array().optional(),
    // Available palettes for the user to choose from. Only needed if there is a
    // colorPicker user input field.
    colorPalettes: z.array(themeColorPaletteNameSchema).optional(),
});
function checkThemeUnknowns(theme) {
    sceneConfigSchema.array().parse(theme.scenes);
    themeStylesSchema.parse(theme.styles);
}
// This is to avoid error TS7056: The inferred type of this node exceeds the
// maximum length the compiler will serialize. We can use this function to parse
// both the theme and the unknown props separately, when needed. Because some
// props are `z.unknown()`, we still get warnings if we try to access scenes
// without first running the theme through this function.
export function parseTheme(theme) {
    const parsedTheme = compilationThemeSchema.parse(theme);
    checkThemeUnknowns(parsedTheme);
    return parsedTheme;
}
export const prefetchAssetPaths = [
    { path: "background.src", iterableRoot: false },
    { path: "background.src.preview", iterableRoot: false },
    { path: "scenes.0.backgroundImageUrl.preview", iterableRoot: false },
    { path: "scenes.0.sidebarRightUrl.preview", iterableRoot: false },
    { path: "scenes.0.sidebarLeftUrl.preview", iterableRoot: false },
    { path: "styles.clips.textVariant.leftUrl.preview", iterableRoot: false },
    { path: "styles.clips.textVariant.centerUrl.preview", iterableRoot: false },
    { path: "styles.clips.textVariant.rightUrl.preview", iterableRoot: false },
    {
        path: "styles.clips.textVariant.backgroundUrl.preview",
        iterableRoot: false,
    },
    { path: "styles.clips.overlay.url.preview", iterableRoot: false },
    {
        path: "styles.clips.betweenTransition.mask",
        iterableRoot: true,
    },
    {
        path: "styles.clips.betweenTransition.overlay",
        iterableRoot: true,
    },
    {
        path: "styles.sceneTransition.mask",
        iterableRoot: true,
    },
    {
        path: "styles.sceneTransition.overlay",
        iterableRoot: true,
    },
];
